import { Locales } from '@axo/config/@types/Locales.types';
import { ConfigService } from '@axo/config/ConfigService';
import termsFI from '@axo/form/feature/terms-and-conditions/generated/terms-and-conditions-fi.json';
import termsNO from '@axo/form/feature/terms-and-conditions/generated/terms-and-conditions-no.json';
import termsSE from '@axo/form/feature/terms-and-conditions/generated/terms-and-conditions-se.json';
import { termsSchema as termsSchema_old } from '@axo/form/feature/terms-and-conditions/src/types/Terms';
import { getTermsVersionString } from '@axo/form/feature/terms-and-conditions/src/util/getTermsVersionString';
import { useTranslation } from '@axo/shared/i18n';
import { MarketCountryCode } from '@axo/shared/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAxoFormContext } from '../../../context/AxoForm/AxoForm';
import { TermsAndConditions } from '../@types/Terms.types';

const termsJson: Record<MarketCountryCode, any> = {
  [MarketCountryCode.Norway]: termsNO,
  [MarketCountryCode.Finland]: termsFI,
  [MarketCountryCode.Sweden]: termsSE,
  [MarketCountryCode.Denmark]: new Error('Not implemented'),
};

// Respective return types for each condition
type UseTermsReturnType<External extends boolean> = External extends false
  ? { loading: boolean; data: any; version: string }
  : {
      loading: boolean;
      data: TermsAndConditions[] | undefined;
      version: string;
    };

export const useTerms = <External extends boolean>(
  external: External
): UseTermsReturnType<External> => {
  const { brand } = ConfigService.get('global');

  const { marketCountry } = useAxoFormContext();
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const termsLang = useRef('');
  const [terms, setTerms] = useState<TermsAndConditions[]>();

  useEffect(() => {
    let ignore = false;

    if (ignore || termsLang.current === locale) return;
    termsLang.current = locale;

    const getTerms = async () => {
      const isProduction = ConfigService.get('env').env === 'production';
      const { domain } = ConfigService.get('site');
      const subdomain = isProduction ? 'one.axogroup.com' : 'one.axo-test.io';

      const { defaultLocale } = ConfigService.get('global').i18n;

      const url = new URL(
        'api/cms/terms-and-conditions',
        `https://${domain}.${subdomain}`
      );

      if (locale !== defaultLocale.split('-').at(-1)?.toLowerCase()) {
        const _locale =
          Locales.find((l) => l.split('-').at(-1)?.toLowerCase() === locale) ||
          Locales[0];
        url.searchParams.append('locale', _locale);
      }

      const request = await fetch(url);
      const terms = await request.json();

      setTerms(terms);
    };

    external && getTerms();

    return () => {
      ignore = true;
    };
  }, [external, i18n.language, locale, terms]);

  const version = useMemo(
    () =>
      terms?.reduce((acc, curr) => {
        const date = new Date(curr._publish_on);
        const year = date.getUTCFullYear();
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = date.getUTCDate().toString().padStart(2, '0');

        const string = `${curr.tc_type}: ${curr.version}.${year}${month}${day}-${brand};`;

        return acc + string;
      }, '') ?? '',
    [terms, brand]
  );

  if (!external) {
    const json = termsSchema_old.parse(termsJson[marketCountry]);

    const termsVersion = getTermsVersionString(termsSchema_old.parse(json));

    return { loading: false, data: json as any, version: termsVersion };
  }

  return { loading: !!terms, data: terms, version };
};
